import {
	CONNECTION_TYPE,
	DOCS_TYPE,
	IConnectionOperation,
	IDocumentsDocs,
	IDocumentsOperation,
	IOperation,
	OPERATION_TYPE,
} from '../../operations/models/operations.models';
import { IWorkflowDetailResponse } from '../models/workflows.models';

/**
 * Utilidades ATTORNEY_POWERS del editor de workflows (NIF-530).
 *
 * Abrir cuando: inferir mode auto/manual desde DOCUMENTS/CONNECTIONS SCRIPTURE,
 * o decidir si el fallback manual va al payload.
 * Sin dependencias Angular — puro y testeable.
 */

export interface AttorneyPowersSources {
	/** DOCUMENTS con doc SCRIPTURE (flujo manual). */
	documentsOp?: IDocumentsOperation;
	/** CONNECTIONS tipo SCRIPTURE (flujo auto). */
	connectionsOp?: IConnectionOperation;
}

/**
 * Localiza en el detalle las ops que representan poderes notariales.
 *
 * @param detail - Detalle del workflow
 */
export function findAttorneyPowersSources(
	detail: Pick<IWorkflowDetailResponse, 'operations'> | undefined,
): AttorneyPowersSources {
	const documentsOp = detail?.operations?.find(
		(op: IOperation) =>
			op.type === OPERATION_TYPE.DOCUMENTS &&
			(op as IDocumentsOperation).config?.docs?.find((doc: IDocumentsDocs) => doc.type === DOCS_TYPE.SCRIPTURE),
	) as IDocumentsOperation | undefined;

	const connectionsOp = detail?.operations?.find(
		(op: IOperation) =>
			op.type === OPERATION_TYPE.CONNECTIONS &&
			(op as IConnectionOperation).config?.type === CONNECTION_TYPE.SCRIPTURE,
	) as IConnectionOperation | undefined;

	return { documentsOp, connectionsOp };
}

/**
 * Patch del form ATTORNEY_POWERS a partir de las fuentes SCRIPTURE del detalle.
 * Si no hay ninguna → `{ available: false }`.
 *
 * @param sources - Resultado de {@link findAttorneyPowersSources}
 */
export function mapAttorneyPowersFormPatch(sources: AttorneyPowersSources): Record<string, unknown> {
	const { documentsOp, connectionsOp } = sources;
	if (!documentsOp && !connectionsOp) {
		return { available: false };
	}
	const scriptureDoc = documentsOp?.config?.docs?.find((doc) => doc.type === DOCS_TYPE.SCRIPTURE);
	return {
		available: true,
		tutorial: documentsOp?.config?.tutorial || true,
		total_tries: scriptureDoc?.total_tries ?? connectionsOp?.config?.total_tries ?? 2,
		// Doc SCRIPTURE ⇒ manual; solo connection ⇒ auto.
		mode: documentsOp ? 'manual' : 'auto',
		fallback: connectionsOp?.config.fallback?.type ?? 'auto',
		fallback_validation: connectionsOp?.config.fallback?.validation ?? 'auto',
		fallback_emails: connectionsOp?.config.fallback?.emails || '',
		uuid: documentsOp?.uuid || connectionsOp?.uuid,
	};
}

/**
 * ¿Se debe enviar fallback manual en CONNECTIONS SCRIPTURE?
 * Requiere compañía con validación manual **y** form en `manual`.
 *
 * @param companyManualValidation - `company.manual_validation`
 * @param fallbackType - Tipo resuelto del form (`manual` | …)
 */
export function shouldIncludeAttorneyManualFallback(
	companyManualValidation: boolean | undefined,
	fallbackType: unknown,
): boolean {
	return companyManualValidation === true && fallbackType === 'manual';
}

/**
 * Bloque `fallback` del payload CONNECTIONS SCRIPTURE (o `undefined`).
 *
 * @param companyManualValidation - Flag de compañía
 * @param fallbackType - Tipo del form
 * @param fallbackValidation - Validación del form
 * @param fallbackEmails - Emails del form
 */
export function buildAttorneyManualFallbackOrUndefined(
	companyManualValidation: boolean | undefined,
	fallbackType: unknown,
	fallbackValidation: unknown,
	fallbackEmails: unknown,
): { emails: unknown; type: unknown; validation: unknown } | undefined {
	if (!shouldIncludeAttorneyManualFallback(companyManualValidation, fallbackType)) {
		return undefined;
	}
	return {
		emails: fallbackValidation === 'manual' ? fallbackEmails : '',
		type: fallbackType,
		validation: fallbackValidation,
	};
}
